import { Fab, LinkButton } from '@/components/elements/forms/buttons';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { useEffect, useState } from 'react';

const baseTranslationKey = 'features.PlacePages.components.TabBar';

const TABS = ['staff', 'reviews', 'about', 'services'];

const TabBar = ({
  top,
  placeId,
  placeName,
  reviews,
  onFavoriteClick,
  hideEmployees,
  showReviews,
}: {
  top?: boolean;
  placeId?: number;
  placeName?: string;
  reviews?: { count: number; rating: number };
  hideEmployees?: boolean;
  onFavoriteClick?: () => void;
  showReviews?: boolean;
}) => {
  const { isMobileView } = useMobileView();
  const user = useAppSelector((state) => state.users.user);
  const isFavorite = user ? user.favorites.indexOf(placeId) !== -1 : false;
  const [activeTab, setActiveTab] = useState('');

  useEffect(() => {
    const handleTabChange = () => {
      const hash = window.location.hash.replace('#', '');

      if (TABS.includes(hash)) {
        setActiveTab(hash);
      }
    };

    window.addEventListener('hashchange', handleTabChange);
    handleTabChange();

    return () => window.removeEventListener('hashchange', handleTabChange);
  }, []);

  return (
    <div
      className={`bg-surfaceColors-surface_l2 drop-shadow-xs ${isMobileView ? 'px-lg h-[64px]' : 'px-md h-[72px]'} ${
        top ? 'flex justify-center' : 'border-border_regular rounded-lg border-b'
      }`}>
      <div className={`flex h-full justify-between ${top ? 'w-[1040px]' : ''}`}>
        {top && !isMobileView && (
          <div className="gap-xxs flex flex-col justify-center">
            <div className="text-l">{placeName}</div>
            <Rating count={reviews?.count ?? 0} rating={reviews?.rating} size={'sm'} />
          </div>
        )}
        <div className={`${isMobileView ? 'gap-lg' : 'gap-xl'} flex items-center`}>
          {!hideEmployees && (
            <a
              href="#staff"
              className={` flex h-full outline-none ${
                activeTab === 'staff' ? 'border-border_selected border-b-2' : ''
              }`}>
              <div className="gap-xs flex items-center">
                {!isMobileView && (
                  <Icon variant="users" color={activeTab === 'staff' ? 'fg_primary' : 'fg_secondary'} />
                )}
                <span className={`${activeTab === 'staff' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                  {_s(`${baseTranslationKey}.personal`)}
                </span>
              </div>
            </a>
          )}
          {showReviews && (
            <a
              href="#reviews"
              className={`flex h-full outline-none ${
                activeTab === 'reviews' ? 'border-border_selected border-b-2' : ''
              }`}>
              <div className="gap-xs flex items-center">
                {!isMobileView && (
                  <Icon variant="star-empty" color={activeTab === 'reviews' ? 'fg_primary' : 'fg_secondary'} />
                )}
                <span className={`${activeTab === 'reviews' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                  {_s(`${baseTranslationKey}.reviews`)}
                </span>
              </div>
            </a>
          )}
          <a
            href="#about"
            className={`flex h-full outline-none ${activeTab === 'about' ? 'border-border_selected border-b-2' : ''}`}>
            <div className="gap-xs flex items-center">
              {!isMobileView && (
                <Icon variant="info-square" color={activeTab === 'about' ? 'fg_primary' : 'fg_secondary'} />
              )}
              <span className={`${activeTab === 'about' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                {_s(`${baseTranslationKey}.about`)}
              </span>
            </div>
          </a>
        </div>
        <div className="gap-xl ml-xs flex items-center justify-end">
          {!isMobileView && (
            <Fab
              size="md"
              onClick={onFavoriteClick}
              aria-label={_s(`${baseTranslationKey}.favorites.${isFavorite ? 'remove' : 'add'}`)}
              variant="secondary"
              icon={<Icon variant={`heart${isFavorite ? '-filled' : ''}`} color="fg_primary" />}></Fab>
          )}

          <LinkButton
            size="md"
            to={'#services'}
            variant="primary"
            block={isMobileView}
            iconNoFilter
            label={_s(`${baseTranslationKey}.services`)}></LinkButton>
        </div>
      </div>
    </div>
  );
};

export default TabBar;
